import React, { useEffect, useState } from 'react';
import { api, AiAnalysis } from '../api/client';

interface Props {
  extractionId: string;
}

// ─── Config ───────────────────────────────────────────────────────────────────
const urgencyConfig: Record<string, { bg: string; text: string; border: string; icon: string; bar: string }> = {
  critical: { bg: '#fef2f2', text: '#dc2626', border: '#fca5a5', icon: '🚨', bar: '#ef4444' },
  high:     { bg: '#fff7ed', text: '#ea580c', border: '#fdba74', icon: '⚠️', bar: '#f97316' },
  medium:   { bg: '#fefce8', text: '#ca8a04', border: '#fde047', icon: '📋', bar: '#eab308' },
  low:      { bg: '#f0fdf4', text: '#16a34a', border: '#86efac', icon: '✅', bar: '#22c55e' },
};

const docTypeLabels: Record<string, string> = {
  insurance_binder: 'Insurance Binder',
  policy_issuance:  'Policy Issuance',
  renewal:          'Renewal',
  cancellation:     'Cancellation',
  endorsement:      'Endorsement',
  claim_notice:     'Claim Notice',
  payment_receipt:  'Payment Receipt',
  audit:            'Audit',
  other:            'Other',
};

const urgencyScore: Record<string, number> = { low: 25, medium: 50, high: 75, critical: 100 };

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconRefresh = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/>
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/>
  </svg>
);

// ─── Sub-components ───────────────────────────────────────────────────────────
const FieldRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f1f5f9', alignItems: 'flex-start' }}>
    <div style={{ width: 150, flexShrink: 0, fontSize: '11px', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.05em', paddingTop: 2 }}>
      {label}
    </div>
    <div style={{ fontSize: '13px', color: 'var(--text)', flex: 1 }}>{value || <span style={{ color: 'var(--text-3)' }}>—</span>}</div>
  </div>
);

const Tag: React.FC<{ children: React.ReactNode; color?: string; textColor?: string }> = ({ children, color = '#e0e7ff', textColor = '#3730a3' }) => (
  <span style={{
    background: color, color: textColor,
    borderRadius: '5px', padding: '2px 8px',
    fontSize: '11px', fontWeight: 600,
    display: 'inline-block', margin: '2px',
  }}>
    {children}
  </span>
);

const KVTable: React.FC<{ items: Array<{ key: string; value: string }> }> = ({ items }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    {items.map((kv, i) => (
      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 10px', background: i % 2 === 0 ? '#f8fafc' : 'transparent', borderRadius: '4px', fontSize: '12px' }}>
        <span style={{ color: 'var(--text-2)' }}>{kv.key}</span>
        <span style={{ fontWeight: 700, color: 'var(--text)' }}>{kv.value}</span>
      </div>
    ))}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const AiAnalysisPanel: React.FC<Props> = ({ extractionId }) => {
  const [analysis, setAnalysis] = useState<AiAnalysis | null>(null);
  const [loading, setLoading]   = useState(true);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [showRaw, setShowRaw]   = useState(false);

  const load = () => {
    setLoading(true);
    api.getAiAnalysis(extractionId)
      .then((data) => { setAnalysis(data); setNotFound(false); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [extractionId]);

  const handleReanalyze = async () => {
    setReanalyzing(true);
    try {
      await api.reanalyze(extractionId);
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const data = await api.getAiAnalysis(extractionId);
          if (data && !data.error) { setAnalysis(data); setNotFound(false); clearInterval(poll); setReanalyzing(false); }
        } catch { /* keep polling */ }
        if (attempts > 15) { clearInterval(poll); setReanalyzing(false); load(); }
      }, 2000);
    } catch { setReanalyzing(false); }
  };

  // ── Header shared ──
  const PanelHeader = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px 16px', borderBottom: '1px solid var(--border)' }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text)' }}>🤖 AI Insurance Analysis</div>
        <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px' }}>Powered by OpenAI</div>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          className="btn-ghost"
          onClick={handleReanalyze}
          disabled={reanalyzing}
          style={{ fontSize: '12px', padding: '5px 12px' }}
        >
          <span className={reanalyzing ? 'anim-spin' : ''} style={{ display: 'inline-block' }}><IconRefresh /></span>
          {reanalyzing ? 'Analyzing…' : 'Re-analyze'}
        </button>
      </div>
    </div>
  );

  // ── Loading ──
  if (loading) {
    return (
      <div className="card">
        <PanelHeader />
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton" style={{ height: i === 0 ? 80 : 50 }} />
          ))}
        </div>
      </div>
    );
  }

  // ── Not found ──
  if (notFound || !analysis) {
    return (
      <div className="card">
        <PanelHeader />
        <div style={{ padding: '48px', textAlign: 'center' }}>
          <div style={{ fontSize: '36px', marginBottom: '12px' }}>🔍</div>
          <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: '6px' }}>No AI analysis yet</div>
          <div style={{ fontSize: '13px', color: 'var(--text-3)', marginBottom: '16px' }}>Run the analysis to extract insurance data from this email</div>
          <button className="btn-primary" onClick={handleReanalyze} disabled={reanalyzing}>
            {reanalyzing ? (
              <><span className="anim-spin" style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid rgba(255,255,255,.3)', borderTopColor: '#fff', borderRadius: '50%' }} /> Analyzing…</>
            ) : 'Run AI Analysis'}
          </button>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (analysis.error) {
    return (
      <div className="card">
        <PanelHeader />
        <div style={{ margin: '16px', background: '#fee2e2', borderRadius: 'var(--radius)', padding: '14px', fontSize: '13px', color: '#dc2626', border: '1px solid #fca5a5' }}>
          <strong>Analysis failed:</strong> {analysis.error}
        </div>
      </div>
    );
  }

  const uc     = urgencyConfig[analysis.urgencyLevel] ?? urgencyConfig.low;
  const fields = analysis.extractedFields ?? {};
  const broker  = fields.broker;
  const premium = fields.premium;
  const score   = urgencyScore[analysis.urgencyLevel] ?? 25;

  return (
    <div className="card">
      <PanelHeader />

      {/* ── Urgency + doc type strip ── */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', background: uc.bg, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '24px' }}>{uc.icon}</span>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>Urgency Level</div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: uc.text, textTransform: 'capitalize' }}>{analysis.urgencyLevel}</div>
          </div>
          {/* Progress bar */}
          <div style={{ width: 100, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${score}%`, height: '100%', background: uc.bar, borderRadius: 3, transition: 'width .6s ease' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {analysis.isInsuranceRelated ? (
            <span style={{ background: '#dbeafe', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '20px', padding: '4px 12px', fontSize: '12px', fontWeight: 700 }}>
              📋 {docTypeLabels[analysis.documentType] ?? analysis.documentType}
            </span>
          ) : (
            <span style={{ background: '#f1f5f9', color: '#475569', border: '1px solid var(--border)', borderRadius: '20px', padding: '4px 12px', fontSize: '12px', fontWeight: 600 }}>
              Not Insurance Related
            </span>
          )}
          {analysis.flagged && (
            <span style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '20px', padding: '4px 12px', fontSize: '12px', fontWeight: 700 }}>
              🚩 Flagged
            </span>
          )}
        </div>

        <div style={{ fontSize: '11px', color: 'var(--text-3)', textAlign: 'right' }}>
          <div>{analysis.modelUsed}</div>
          <div>{(analysis.inputTokens ?? 0).toLocaleString()} in · {(analysis.outputTokens ?? 0).toLocaleString()} out tokens</div>
          {analysis.processingMs && <div>{(analysis.processingMs / 1000).toFixed(1)}s</div>}
        </div>
      </div>

      <div style={{ padding: '20px' }}>

        {/* ── Summary ── */}
        <div style={{ background: '#f0f9ff', borderRadius: 'var(--radius)', padding: '14px 16px', borderLeft: '3px solid #0ea5e9', marginBottom: '16px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '6px' }}>Summary</div>
          <p style={{ fontSize: '13px', color: '#0c4a6e', lineHeight: '1.65', margin: 0 }}>{analysis.summary}</p>
        </div>

        {/* ── Flags ── */}
        {analysis.flags.length > 0 && (
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: '16px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#c2410c', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '8px' }}>
              ⚠ Flags ({analysis.flags.length})
            </div>
            {analysis.flags.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '13px', color: '#9a3412', marginBottom: '4px' }}>
                <span style={{ color: '#f97316', fontWeight: 700 }}>•</span><span>{f}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Action items ── */}
        {analysis.actionItems.length > 0 && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: '16px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '8px' }}>
              ✅ Action Items ({analysis.actionItems.length})
            </div>
            {analysis.actionItems.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '10px', fontSize: '13px', color: '#1e3a8a', marginBottom: '6px', alignItems: 'flex-start' }}>
                <span style={{ background: '#2563eb', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>
                  {i + 1}
                </span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Extracted policy fields ── */}
        {analysis.isInsuranceRelated && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid var(--border)', fontSize: '11px', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
              Extracted Policy Fields
            </div>
            <div style={{ padding: '0 16px' }}>
              <FieldRow label="Policy Number"    value={fields.policyNumber} />
              <FieldRow label="Insured Name"     value={fields.insuredName} />
              <FieldRow label="Insured Address"  value={fields.insuredAddress} />
              <FieldRow label="Carrier"          value={fields.carrier} />
              <FieldRow label="Effective Date"   value={fields.effectiveDate} />
              <FieldRow label="Expiration Date"  value={fields.expirationDate} />
              <FieldRow label="Risk Description" value={fields.riskDescription} />

              {premium && (premium.amount != null || premium.currency) && (
                <FieldRow label="Premium" value={
                  <span style={{ fontWeight: 700, color: '#059669', fontSize: '14px' }}>
                    {[premium.currency, premium.amount != null ? premium.amount.toLocaleString() : null].filter(Boolean).join(' ')}
                    {premium.frequency ? ` / ${premium.frequency}` : ''}
                  </span>
                } />
              )}

              {broker && (broker.name || broker.agency) && (
                <FieldRow label="Broker" value={
                  <div>
                    {broker.name   && <div style={{ fontWeight: 600 }}>{broker.name}</div>}
                    {broker.agency && <div style={{ color: 'var(--text-2)' }}>{broker.agency}</div>}
                    {broker.phone  && <div style={{ color: 'var(--text-3)', fontSize: '12px' }}>📞 {broker.phone}</div>}
                    {broker.email  && <div style={{ color: 'var(--text-3)', fontSize: '12px' }}>✉ {broker.email}</div>}
                  </div>
                } />
              )}

              {(fields.coverageTypes?.length ?? 0) > 0 && (
                <FieldRow label="Coverage Types" value={
                  <div>{fields.coverageTypes!.map((t, i) => <Tag key={i} color="#dbeafe" textColor="#1e40af">{t}</Tag>)}</div>
                } />
              )}

              {(fields.coverageLimits?.length ?? 0) > 0 && (
                <FieldRow label="Coverage Limits" value={<KVTable items={fields.coverageLimits!} />} />
              )}

              {(fields.deductibles?.length ?? 0) > 0 && (
                <FieldRow label="Deductibles" value={<KVTable items={fields.deductibles!} />} />
              )}

              {(fields.endorsements?.length ?? 0) > 0 && (
                <FieldRow label="Endorsements" value={
                  <div>{fields.endorsements!.map((en, i) => <Tag key={i} color="#d1fae5" textColor="#065f46">{en}</Tag>)}</div>
                } />
              )}
            </div>
          </div>
        )}

        {/* ── Raw JSON ── */}
        <div style={{ marginTop: '16px' }}>
          <button
            className="btn-ghost"
            onClick={() => setShowRaw((o) => !o)}
            style={{ fontSize: '12px', padding: '5px 12px' }}
          >
            {showRaw ? '▾ Hide' : '▸ Show'} raw JSON
          </button>
          {showRaw && (
            <pre style={{
              marginTop: '10px', background: '#0f172a', color: '#e2e8f0',
              borderRadius: 'var(--radius)', padding: '16px', fontSize: '11px',
              lineHeight: '1.6', overflow: 'auto', maxHeight: '400px',
            }}>
              {JSON.stringify(analysis, null, 2)}
            </pre>
          )}
        </div>

      </div>
    </div>
  );
};

export default AiAnalysisPanel;
